import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import axios from "axios";
import { X, ShoppingCart, Check } from "lucide-react";
import { DigitalPackageStatus, DigitalProductStatus, BrandButton, ProcessSteps } from "../../../../components/ui/index.js";
import { useCartContext } from "../../../../components/providers/CartProvider.jsx";
import { buildOwnerJourney, pickCanonicalPackage } from "../../../../domain/digital/packages.js";

/**
 * DigitalPriceModal — cálculo/estado del producto digital para el propietario.
 *
 * Consulta la versión del diseño (si existe) y permite (re)calcular el precio,
 * siempre server-side. No ofrece descarga: la descarga comercial llega con
 * MVP-05 (entitlement tras pago confirmado). Desde MVP-02, un paquete
 * aprobado sí puede añadirse al carrito propio para pagarlo por Bizum; añadir
 * al carrito no concede acceso al ZIP. Reutiliza el componente genérico
 * DigitalProductStatus.
 *
 * Se monta con un portal a `document.body`: las tarjetas de modelo tienen
 * `hover:scale`/`hover:-translate-y` (transform), y cualquier transform en un
 * ancestro crea un nuevo containing block para `position: fixed`, atrapando el
 * modal dentro de la tarjeta o de la lista en vez de centrarlo en la ventana.
 * El portal lo saca de ese árbol para que siempre aparezca centrado con el
 * fondo oscuro translúcido cubriendo toda la pantalla, sin importar desde
 * dónde se abra.
 */
export default function DigitalPriceModal({ keyringId, open, onClose }) {
    const [version, setVersion] = useState(null);
    const [uiState, setUiState] = useState("loading"); // loading|empty|error|<state>
    const [busy, setBusy] = useState(false);
    const [pkg, setPkg] = useState(null);
    const [packageState, setPackageState] = useState("empty");
    const [addingToCart, setAddingToCart] = useState(false);
    const [addedToCart, setAddedToCart] = useState(false);
    const { addDigitalToCart } = useCartContext();

    const loadPackage = useCallback(async (sourceVersionId) => {
        if (!sourceVersionId) {
            setPkg(null);
            setPackageState("empty");
            return;
        }
        try {
            const { data } = await axios.get("/digital-packages/mine/", { withCredentials: true });
            const found = pickCanonicalPackage(data, sourceVersionId);
            setPkg(found);
            setPackageState(found ? found.state : "empty");
        } catch (e) {
            setPackageState("error");
        }
    }, []);

    const load = useCallback(async () => {
        setUiState("loading");
        try {
            const { data } = await axios.get("/digital-products/mine/", { withCredentials: true });
            // La API ordena de más reciente a más antigua. `find` conserva ese
            // orden y la normalización evita perder coincidencias number/string.
            const latest = (data || []).find(
                (candidate) => String(candidate.design_id) === String(keyringId)
            ) || null;
            if (!latest) {
                setVersion(null);
                setUiState("empty");
                await loadPackage(null);
            } else {
                setVersion(latest);
                setUiState(latest.state);
                await loadPackage(latest.id);
            }
        } catch (e) {
            setUiState("error");
        }
    }, [keyringId, loadPackage]);

    useEffect(() => {
        if (open) {
            setAddedToCart(false);
            load();
        }
    }, [open, load]);

    const handleAddToCart = useCallback(async () => {
        if (!pkg?.id) return;
        setAddingToCart(true);
        const result = await addDigitalToCart(pkg.id);
        setAddingToCart(false);
        if (result.ok) setAddedToCart(true);
    }, [pkg, addDigitalToCart]);

    useEffect(() => {
        if (!open) return undefined;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [open]);

    const calculate = useCallback(async () => {
        setBusy(true);
        try {
            const { data } = await axios.post(
                "/digital-products/calculate/",
                { design_id: keyringId },
                { withCredentials: true }
            );
            setVersion(data);
            setUiState(data.state);
        } catch (e) {
            setUiState("error");
        } finally {
            setBusy(false);
        }
    }, [keyringId]);

    if (!open) return null;

    return createPortal(
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            role="dialog"
            aria-modal="true"
            aria-label="Precio del producto digital"
            onClick={onClose}
        >
            <div
                className="w-full max-w-md rounded-2xl bg-base-100 p-5 shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="mb-3 flex items-center justify-between">
                    <h2 className="text-lg font-bold text-base-content">Producto digital</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Cerrar"
                        className="rounded-lg p-1 text-base-content/60 hover:bg-base-content/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                        <X className="h-5 w-5" aria-hidden />
                    </button>
                </div>

                <DigitalProductStatus
                    state={uiState}
                    calculatedPrice={version?.calculated_price}
                    finalPrice={version?.final_price}
                    currency={version?.currency || "EUR"}
                    backgroundCount={version?.background_count ?? null}
                    volumeCm3={version?.volume_cm3 ?? null}
                    priceBreakdown={version?.price_breakdown || null}
                    busy={busy}
                    onCalculate={calculate}
                    onRetry={uiState === "error" ? load : calculate}
                />

                {uiState !== "loading" && uiState !== "error" && (
                    <div className="mt-4 rounded-xl border border-base-300 bg-base-200/40 p-3">
                        <h3 className="text-xs uppercase tracking-widest text-base-content/50 font-semibold mb-3">
                            Tu producto digital, paso a paso
                        </h3>
                        <ProcessSteps
                            compact
                            steps={buildOwnerJourney({ uiState, packageState, pkg })}
                        />
                    </div>
                )}

                {uiState === "aprobado" && (
                    <DigitalPackageStatus
                        className="mt-3"
                        state={packageState}
                        hasTemplate={Boolean(pkg?.has_template)}
                        onRetry={packageState === "error" ? () => loadPackage(version?.id) : undefined}
                    />
                )}

                {packageState === "aprobado" && (
                    <div className="mt-3">
                        <BrandButton
                            type="button"
                            className="w-full"
                            onClick={handleAddToCart}
                            disabled={addingToCart || addedToCart}
                            aria-label="Añadir este paquete digital al carrito"
                        >
                            {addedToCart ? (
                                <>
                                    <Check className="h-4 w-4" aria-hidden /> Añadido al carrito
                                </>
                            ) : (
                                <>
                                    <ShoppingCart className="h-4 w-4" aria-hidden />
                                    {addingToCart ? "Añadiendo..." : "Añadir al carrito"}
                                </>
                            )}
                        </BrandButton>
                        <p className="mt-2 text-xs text-base-content/60">
                            El archivo se habilitará para descarga después de confirmar el pago, no al añadirlo al carrito.
                        </p>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
}

DigitalPriceModal.propTypes = {
    keyringId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    open: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
};
